import type Phaser from "phaser";
import { MAX_EMITTERS_PER_VIEW, MAX_PARTICLES_PER_VIEW, emitterSettings, sampleAlpha, sampleRgb, type Attachment, type AttachmentShape, type EffectPreset, type EmitterSettings } from "daedalus-core/effects";

/**
 * The particle effects of a view (ADR-141 decisions F12 to F16): one Phaser `ParticleEmitter` per sub-kind of each attached
 * preset, driven by the engine-neutral numbers of `emitterSettings`. Emitters are pooled per preset sub-kind: attaching takes
 * one and detaching stops it, clears it and returns it, so after warm-up attaching and detaching creates no emitter and no
 * particle (Phaser's emitter keeps its own pool of dead particles). Nothing runs per frame in this class: each emitter's
 * timers and callbacks are set when it is made, a shape's emit zone is one reused source whose shape is swapped on take, and
 * `moveTo` writes two numbers.
 *
 * "Effects: Full, Reduced, Off" is the `detail`: Reduced halves every rate and pool, Off holds no emitter (the placements are
 * remembered, so turning Effects back on brings them back). A view holds at most `maxEmitters` emitters and `maxParticles`
 * particles of pool; an attachment that would pass either waits, in the order it came, until others leave.
 */

/**
 * How many points of a colour ramp and an alpha ramp the engine interpolates through; nine includes the 3/8 stop of the
 * burnt board's embers exactly.
 */
const COLOUR_SAMPLES = 8;
const ALPHA_SAMPLES = 9;

/**
 * The key of the one-pixel white texture every particle is drawn from, tinted and scaled per particle.
 */
export const PIXEL_TEXTURE = "daedalus:effect-pixel";

/**
 * The setting "Effects: Full, Reduced, Off".
 */
export type EffectsDetail = "full" | "reduced" | "off";

/**
 * The depths effects draw at: behind or in front of their props, and the emissive layer above the darkness.
 */
export interface EffectDepths {
  readonly behind: number;
  readonly front: number;
  readonly emissive: number;
}

/**
 * Looks a preset up by id.
 */
export type PresetLookup = (id: string) => EffectPreset | undefined;

/**
 * What the view is made with.
 */
export interface EffectViewOptions {
  readonly scene: Phaser.Scene;
  readonly presets: PresetLookup;
  readonly detail: EffectsDetail;
  readonly depths: EffectDepths;
  readonly maxEmitters?: number;
  readonly maxParticles?: number;
}

/**
 * What a placement may ask beyond its attachment.
 */
export interface AttachOptions {
  readonly advanceMs?: number;
}

/**
 * What the view holds, for a test or a bench page.
 */
export interface EffectViewStats {
  readonly placements: number;
  readonly active: number;
  readonly pooled: number;
  readonly waiting: number;
  readonly created: number;
  readonly budget: number;
}

/**
 * A cheap seeded source of cosmetic randomness (xorshift32): spawn points and sizes are never compared across machines, so
 * the view owns its numbers and nothing here touches `Math.random`.
 */
export class CosmeticRandom {
  private state: number;

  /**
   * @param seed Any non-zero integer.
   */
  public constructor(seed = 0x2545f491) {
    this.state = seed >>> 0 || 1;
  }

  /**
   * The next number.
   *
   * @returns A number from 0 up to but not including 1.
   */
  public frac(): number {
    let x = this.state;
    x ^= x << 13;
    x >>>= 0;
    x ^= x >>> 17;
    x ^= x << 5;
    x >>>= 0;
    this.state = x;
    return x / 4294967296;
  }
}

/**
 * The emit zone source one emitter reuses: a random point on the attached shape, relative to the emitter's position.
 */
class ShapeSource {
  public shape: AttachmentShape = { at: "point", x: 0, y: 0 };

  /**
   * @param random The view's source of cosmetic randomness.
   */
  public constructor(private readonly random: CosmeticRandom) {}

  /**
   * Writes a random point of the shape into `point`; an area picks one of its rectangles by area.
   *
   * @param point The point to write.
   */
  public getRandomPoint(point: { x: number; y: number }): void {
    const shape = this.shape;
    if (shape.at === "point") {
      point.x = shape.x;
      point.y = shape.y;
    } else if (shape.at === "edge") {
      const t = this.random.frac();
      point.x = shape.x1 + (shape.x2 - shape.x1) * t;
      point.y = shape.y1 + (shape.y2 - shape.y1) * t;
    } else {
      let total = 0;
      for (const rect of shape.cells) total += rect[2] * rect[3];
      let pick = this.random.frac() * total;
      let chosen = shape.cells[0];
      for (const rect of shape.cells) {
        chosen = rect;
        pick -= rect[2] * rect[3];
        if (pick <= 0) break;
      }
      point.x = (chosen?.[0] ?? 0) + this.random.frac() * (chosen?.[2] ?? 0);
      point.y = (chosen?.[1] ?? 0) + this.random.frac() * (chosen?.[3] ?? 0);
    }
  }
}

/**
 * One pooled emitter and the source it draws its spawn points from.
 */
interface Pooled {
  readonly emitter: Phaser.GameObjects.Particles.ParticleEmitter;
  readonly source: ShapeSource;
  readonly poolKey: string;
  budget: number;
  timer: Phaser.Time.TimerEvent | null;
  zones: Phaser.GameObjects.Particles.Zones.DeathZone[] | null;
}

/**
 * A region of the world where particles are not drawn: any object that says whether a world point lies in it. The engine tests
 * every live particle against it once a frame, so `contains` must answer in constant time and allocate nothing.
 */
export interface CullArea {
  contains(x: number, y: number): boolean;
}

/**
 * One attachment placed in the view.
 */
interface Placement {
  readonly attachment: Attachment;
  readonly preset: EffectPreset | undefined;
  readonly options: AttachOptions;
  x: number;
  y: number;
  emitters: Pooled[];
}

/**
 * The view's effects.
 */
export class EffectView {
  private readonly scene: Phaser.Scene;
  private readonly presets: PresetLookup;
  private readonly depths: EffectDepths;
  private readonly maxEmitters: number;
  private readonly maxParticles: number;
  private readonly random = new CosmeticRandom();
  private detail: EffectsDetail;
  private paused = false;
  private readonly placements = new Map<string, Placement>();
  private readonly pools = new Map<string, Pooled[]>();
  private activeEmitters = 0;
  private activeBudget = 0;
  private pooledCount = 0;
  private created = 0;
  private cull: CullArea | null = null;

  /**
   * @param options What the view is made with.
   */
  public constructor(options: EffectViewOptions) {
    this.scene = options.scene;
    this.presets = options.presets;
    this.depths = options.depths;
    this.detail = options.detail;
    this.maxEmitters = options.maxEmitters ?? MAX_EMITTERS_PER_VIEW;
    this.maxParticles = options.maxParticles ?? MAX_PARTICLES_PER_VIEW;
    this.ensurePixelTexture();
  }

  /**
   * Places an attachment in the view; an attachment under a key already in use replaces the old one.
   *
   * @param key A key the caller detaches by.
   * @param attachment Which preset and from where, relative to `(x, y)`.
   * @param x The world x of the attachment's origin.
   * @param y The world y of the attachment's origin.
   * @param options Optionally a head start for the emitters, in milliseconds.
   * @returns True when emitters are running for it now; false when Effects are Off, the preset is a light or is unknown, or
   *   the view's caps hold it back until others leave.
   */
  public attach(key: string, attachment: Attachment, x: number, y: number, options: AttachOptions = {}): boolean {
    this.detach(key);
    const placement: Placement = { attachment, preset: this.presets(attachment.preset), options, x, y, emitters: [] };
    this.placements.set(key, placement);
    return this.start(placement);
  }

  /**
   * Takes an attachment out of the view and returns its emitters to their pools. A key not in use changes nothing.
   *
   * @param key The key it was attached with.
   */
  public detach(key: string): void {
    const placement = this.placements.get(key);
    if (placement === undefined) return;
    this.placements.delete(key);
    if (placement.emitters.length > 0) {
      this.stop(placement);
      this.admitWaiting();
    }
  }

  /**
   * Moves an attachment's origin; a screen-anchored effect calls this each frame.
   *
   * @param key The key it was attached with.
   * @param x The new world x.
   * @param y The new world y.
   */
  public moveTo(key: string, x: number, y: number): void {
    const placement = this.placements.get(key);
    if (placement === undefined) return;
    placement.x = x;
    placement.y = y;
    for (const pooled of placement.emitters) pooled.emitter.setPosition(x, y);
  }

  /**
   * Sets "Effects: Full, Reduced, Off". A change gives every emitter back and takes them again at the new detail.
   *
   * @param detail The setting.
   */
  public setDetail(detail: EffectsDetail): void {
    if (detail === this.detail) return;
    this.detail = detail;
    for (const placement of this.placements.values()) {
      if (placement.emitters.length > 0) this.stop(placement);
    }
    this.destroyPools();
    this.admitWaiting();
  }

  /**
   * Sets the region where no particle shows: every running emitter kills a particle that is in it (a death zone, tested against
   * the particle's world position), and a particle born in it is clear for its one frame before the zone takes it. An emitter
   * attached later is armed the same. A new area replaces the old one, and null clears it.
   *
   * @param area The region, or null for none.
   */
  public setCull(area: CullArea | null): void {
    if (area === this.cull) return;
    this.cull = area;
    for (const placement of this.placements.values()) {
      for (const pooled of placement.emitters) {
        this.disarm(pooled);
        this.arm(pooled);
      }
    }
  }

  /**
   * Pauses or resumes every emitter with the shared clock.
   *
   * @param paused Whether effects stand still.
   */
  public setPaused(paused: boolean): void {
    if (paused === this.paused) return;
    this.paused = paused;
    for (const placement of this.placements.values()) {
      for (const pooled of placement.emitters) {
        if (paused) pooled.emitter.pause();
        else pooled.emitter.resume();
      }
    }
  }

  /**
   * How many emitters run now; reading it allocates nothing, so a frame may.
   *
   * @returns The count.
   */
  public get activeCount(): number {
    return this.activeEmitters;
  }

  /**
   * The pool budget of the running emitters, in particles; reading it allocates nothing.
   *
   * @returns The budget.
   */
  public get budgetInUse(): number {
    return this.activeBudget;
  }

  /**
   * What the view holds.
   *
   * @returns The counts.
   */
  public get stats(): EffectViewStats {
    let waiting = 0;
    for (const placement of this.placements.values()) {
      if (placement.emitters.length === 0 && this.runnable(placement)) waiting += 1;
    }
    return { placements: this.placements.size, active: this.activeEmitters, pooled: this.pooledCount, waiting, created: this.created, budget: this.activeBudget };
  }

  /**
   * Destroys every emitter, held or pooled, and forgets every placement.
   */
  public dispose(): void {
    for (const placement of this.placements.values()) {
      if (placement.emitters.length > 0) this.stop(placement);
    }
    this.placements.clear();
    this.destroyPools();
  }

  /**
   * Whether a placement would have emitters at the present detail.
   *
   * @param placement The placement.
   * @returns True when its preset is a particle preset and Effects are not Off.
   */
  private runnable(placement: Placement): boolean {
    return this.detail !== "off" && placement.preset?.kind === "particles";
  }

  /**
   * Gives a placement its emitters if it is runnable and the caps allow.
   *
   * @param placement The placement.
   * @returns True when it now has emitters.
   */
  private start(placement: Placement): boolean {
    if (!this.runnable(placement) || placement.preset === undefined) return false;
    const settings = emitterSettings(placement.preset, placement.attachment, this.detail === "reduced" ? 0.5 : 1);
    let budget = 0;
    for (const entry of settings) budget += entry.maxParticles;
    if (this.activeEmitters + settings.length > this.maxEmitters || this.activeBudget + budget > this.maxParticles) return false;
    const preset = placement.preset;
    for (const entry of settings) placement.emitters.push(this.take(preset, entry, placement));
    this.activeEmitters += settings.length;
    this.activeBudget += budget;
    return true;
  }

  /**
   * Gives an emitter the view's cull area as a death zone, when there is one.
   *
   * @param pooled The emitter.
   */
  private arm(pooled: Pooled): void {
    if (this.cull === null) return;
    pooled.zones = pooled.emitter.addDeathZone({ type: "onEnter", source: this.cull });
  }

  /**
   * Takes an emitter's death zones back.
   *
   * @param pooled The emitter.
   */
  private disarm(pooled: Pooled): void {
    if (pooled.zones === null) return;
    for (const zone of pooled.zones) pooled.emitter.removeDeathZone(zone);
    pooled.zones = null;
  }

  /**
   * Returns a placement's emitters to their pools.
   *
   * @param placement The placement.
   */
  private stop(placement: Placement): void {
    for (const pooled of placement.emitters) {
      this.disarm(pooled);
      pooled.timer?.remove(false);
      pooled.timer = null;
      pooled.emitter.stop(true);
      pooled.emitter.setVisible(false);
      this.activeBudget -= pooled.budget;
      let pool = this.pools.get(pooled.poolKey);
      if (pool === undefined) {
        pool = [];
        this.pools.set(pooled.poolKey, pool);
      }
      pool.push(pooled);
      this.pooledCount += 1;
    }
    this.activeEmitters -= placement.emitters.length;
    placement.emitters = [];
  }

  /**
   * Starts the placements the caps held back, in the order they were attached, while they fit.
   */
  private admitWaiting(): void {
    for (const placement of this.placements.values()) {
      if (placement.emitters.length === 0 && this.runnable(placement)) this.start(placement);
    }
  }

  /**
   * Takes an emitter for one sub-kind from its pool, making one only when the pool is empty.
   *
   * @param preset The preset.
   * @param settings The sub-kind's settings.
   * @param placement The placement it is for.
   * @returns The emitter, running.
   */
  private take(preset: EffectPreset, settings: EmitterSettings, placement: Placement): Pooled {
    const poolKey = `${preset.id}#${settings.subKind}`;
    const reused = this.pools.get(poolKey)?.pop();
    if (reused !== undefined) this.pooledCount -= 1;
    const pooled = reused ?? this.make(poolKey, settings);
    pooled.budget = settings.maxParticles;
    pooled.source.shape = settings.shape;
    const emitter = pooled.emitter;
    emitter.setPosition(placement.x, placement.y);
    emitter.setDepth(settings.emissive ? this.depths.emissive : settings.behind ? this.depths.behind : this.depths.front);
    emitter.setVisible(true);
    this.arm(pooled);
    if (this.paused) emitter.pause();
    else emitter.resume();
    const begin = (): void => {
      pooled.timer = null;
      emitter.start(placement.options.advanceMs ?? 0);
      if (settings.burst > 0) emitter.explode(settings.burst);
    };
    if (settings.startDelayMs > 0) pooled.timer = this.scene.time.delayedCall(settings.startDelayMs, begin);
    else begin();
    return pooled;
  }

  /**
   * Makes one emitter for a sub-kind; its callbacks and shape source are set here and never again.
   *
   * @param poolKey The pool it returns to.
   * @param settings The sub-kind's settings.
   * @returns The new, stopped emitter.
   */
  private make(poolKey: string, settings: EmitterSettings): Pooled {
    const source = new ShapeSource(this.random);
    const sizes = settings.sizes;
    let total = 0;
    for (const size of sizes) total += size.weight;
    const pickSize = (): number => {
      let pick = this.random.frac() * total;
      for (const size of sizes) {
        pick -= size.weight;
        if (pick <= 0) return size.px;
      }
      return sizes[sizes.length - 1]?.px ?? 1;
    };
    const sway = settings.swayAmplitude > 0 && settings.swayHz > 0;
    const omega = (Math.PI * 2 * settings.swayHz) / 1000;
    const loop = this.scene.game.loop;
    const config: Phaser.Types.GameObjects.Particles.ParticleEmitterConfig = {
      emitting: false,
      frequency: 1000 / settings.ratePerSecond,
      quantity: 1,
      maxAliveParticles: settings.maxParticles,
      lifespan: { min: settings.life[0], max: settings.life[1] },
      speed: { min: settings.speed[0], max: settings.speed[1] },
      angle: { min: settings.angle[0], max: settings.angle[1] },
      gravityY: settings.gravity,
      scaleX: { onEmit: pickSize },
      scaleY: settings.streak > 0 ? { onEmit: (particle?: Phaser.GameObjects.Particles.Particle) => (particle?.scaleX ?? 1) * (1 + settings.streak) } : { onEmit: pickSize },
      rotate: settings.streak > 0 ? (settings.angle[0] + settings.angle[1]) / 2 - 90 : 0,
      color: sampleRgb(settings.colours, COLOUR_SAMPLES),
      alpha: { values: sampleAlpha(settings.alpha, ALPHA_SAMPLES) },
      blendMode: settings.blend === "add" ? "ADD" : "NORMAL",
      emitZone: { type: "random", source },
      /**
       * Clears a particle born in the cull area for its one frame, before the area's death zone takes it on the next update.
       *
       * @param particle The particle just emitted.
       */
      emitCallback: (particle: Phaser.GameObjects.Particles.Particle): void => {
        const cull = this.cull;
        if (cull !== null && cull.contains(particle.worldPosition.x, particle.worldPosition.y)) particle.alpha = 0;
      },
    };
    if (sway) {
      config.x = {
        onUpdate: (particle: Phaser.GameObjects.Particles.Particle, _key: string, _t: number, value: number): number => {
          const elapsed = particle.lifeT * particle.life;
          return value + settings.swayAmplitude * (Math.sin(omega * elapsed) - Math.sin(omega * (elapsed - loop.delta)));
        },
      };
    }
    const emitter = this.scene.add.particles(0, 0, PIXEL_TEXTURE, config);
    emitter.stop();
    this.created += 1;
    return { emitter, source, poolKey, budget: settings.maxParticles, timer: null, zones: null };
  }

  /**
   * Makes the one-pixel white texture once.
   */
  private ensurePixelTexture(): void {
    const textures = this.scene.textures;
    if (textures.exists(PIXEL_TEXTURE)) return;
    const canvas = textures.createCanvas(PIXEL_TEXTURE, 1, 1);
    const context = canvas?.getContext();
    if (canvas === null || context === undefined) return;
    context.fillStyle = "white";
    context.fillRect(0, 0, 1, 1);
    canvas.refresh();
  }

  /**
   * Destroys every pooled emitter.
   */
  private destroyPools(): void {
    for (const pool of this.pools.values()) {
      for (const pooled of pool) pooled.emitter.destroy();
    }
    this.pools.clear();
    this.pooledCount = 0;
  }
}
